import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from './api';
import { Confirm, type ConfirmSpec } from './ui';

/**
 * "Text schedules…" (prototype Week summary). Texts show the POSTED schedule (what employees see in the
 * app), never unposted edits. Pick the whole week or one day, and Everyone or "Only changes since last
 * text"; untick anyone you don't want to text; click a name to see their exact text.
 */
interface Row { id: number; name: string; phone: string | null; body: string; works: boolean; block: string; texted: boolean; changed: boolean; delivery: string | null }
export interface TextsResp {
  draft: boolean; unposted: number; anyBefore: boolean; configured: boolean; canSend: boolean;
  scopes: { v: string; label: string }[]; rows: Row[]; log: string[];
}

export function useTextLog(weekId: number | null, ver: number) {
  const [log, setLog] = useState<string[]>([]);
  useEffect(() => {
    if (!weekId) { setLog([]); return; }
    get<TextsResp>(`/api/texts?week=${weekId}&scope=week`).then((r) => setLog(r.log)).catch(() => setLog([]));
  }, [weekId, ver]);
  return log;
}

function fmtPhone(p: string | null) {
  const m = p ? /^\+1(\d{3})(\d{3})(\d{4})$/.exec(p) : null;
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '—';
}

export function TextSchedules({ weekId, storeName, only, onClose }: { weekId: number; storeName: string; only?: number; onClose: () => void }) {
  const [scope, setScope] = useState('week');
  const [who, setWho] = useState<'all' | 'changed'>('all');
  const [skip, setSkip] = useState<Record<number, boolean>>({});
  const [preview, setPreview] = useState<number | null>(only ?? null);
  const [d, setD] = useState<TextsResp | null>(null);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<Record<number, { status: 'sent' | 'failed'; error?: string }> | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setD(await get<TextsResp>(`/api/texts?week=${weekId}&scope=${scope}`)); setErr(''); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load the list.'); }
  }, [weekId, scope]);
  useEffect(() => { void load(); }, [load]);
  // after sending, check back for Twilio's delivery reports for a couple of minutes
  useEffect(() => {
    if (!done) return;
    let n = 0; const x = setInterval(() => { if (++n > 24) clearInterval(x); else void load(); }, 5000);
    return () => clearInterval(x);
  }, [done, load]);
  // "Text" on one person's row: everyone else starts unticked
  useEffect(() => {
    if (only && d) setSkip((s) => (Object.keys(s).length ? s : Object.fromEntries(d.rows.filter((r) => r.id !== only).map((r) => [r.id, true]))));
  }, [only, d]);

  const pick = !done;
  const rows = (d?.rows ?? []).map((r) => {
    const eligible = !!d && !r.block && !d.draft && d.canSend && (who === 'all' || r.changed);
    const res = done?.[r.id];
    return {
      ...r, eligible, on: eligible && !skip[r.id] && pick,
      note: r.block || (!r.works ? `Not working ${scope === 'week' ? 'this week' : 'that day'} (text says off)` : !r.texted ? 'Not texted yet' : r.changed ? 'Changed since last text' : 'No change since last text'),
      noteCls: r.block ? 'smsnote bad' : r.changed && r.texted ? 'smsnote chg' : 'smsnote',
      // Failed (Twilio refused it), else what Twilio last reported: Sent → Delivered / Not delivered
      status: res?.status === 'failed' ? `Failed${res.error ? ': ' + res.error : ''}` : r.delivery ?? (res ? 'Sent' : ''),
      statusCls: 'smsstat ' + (res?.status === 'failed' || r.delivery === 'Not delivered' ? 'failed' : r.delivery === 'Delivered' ? 'delivered' : ''),
    };
  });
  const sendIds = rows.filter((r) => r.on).map((r) => r.id);
  const pv = rows.find((r) => r.id === preview);
  const scopeName = scope === 'week' ? 'this week’s schedule' : (d?.scopes.find((s) => s.v === scope)?.label.replace(/^One day: (\w+).*/, '$1’s schedule') ?? 'that day’s schedule');
  const send = async (ids: number[]) => {
    setBusy(true);
    try {
      const r = await post<{ results: Record<number, { status: 'sent' | 'failed'; error?: string }> }>('/api/texts/send', { weekId, scope, ids });
      setDone((x) => ({ ...(x ?? {}), ...r.results })); await load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'The texts didn’t go out. Try again.'); }
    finally { setBusy(false); }
  };
  const results = done ? Object.values(done) : [];
  const failed = results.filter((x) => x.status === 'failed').length;

  return (
    <div className="modalbg" role="presentation"><div className="modal widemodal" role="dialog" aria-modal="true" aria-labelledby="sms-title">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px' }}>
        <b id="sms-title" style={{ fontSize: 18 }}>Text schedules · {storeName}</b><span style={{ flex: '1 1 auto' }} />
        {pick && d && <>
          <select className="sel-sm" value={scope} aria-label="What to text" onChange={(e) => { setScope(e.target.value); setPreview(null); }}>
            {d.scopes.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}</select>
          <span role="group" aria-label="Who gets a text" style={{ display: 'inline-flex', gap: 6 }}>
            <button className={'pill' + (who === 'all' ? ' on' : '')} onClick={() => setWho('all')}>Everyone</button>
            <button className={'pill' + (who === 'changed' ? ' on' : '')} onClick={() => setWho('changed')}>Only changes since last text</button></span>
        </>}
      </div>
      {pick && <div style={{ fontSize: 13, color: '#4A5561' }}>Untick anyone you don’t want to text. Click a name to see the exact message. People without a mobile number, or who said no to texts or replied STOP, can’t be texted.</div>}
      {d?.draft && <div className="sharebar" style={{ background: '#FFE07A', border: '2px solid #D99A00', color: '#3A2A00', fontWeight: 600 }}>⚠ This week is still a draft, so employees can’t see it yet and it can’t be texted. Press Post week first.</div>}
      {d && !d.draft && d.unposted > 0 && pick && <div className="sharebar" style={{ background: '#FFE07A', border: '2px solid #D99A00', color: '#3A2A00', fontWeight: 600 }}>⚠ {d.unposted} {d.unposted === 1 ? 'person has' : 'people have'} changes that aren’t posted yet. Texts show the posted schedule (what employees see in the app). To include the changes, close this, press Post changes, then text “Only changes since last text”.</div>}
      {d && !d.configured && <div className="sharebar" style={{ background: '#FCEFD9', borderColor: '#EBC98A', color: '#5C3A05' }}>Texting isn’t set up on this server yet (the Twilio settings). You can look through the list, but nothing can be sent.</div>}
      {pick && who === 'changed' && d && !d.anyBefore && <div style={{ fontSize: 13, color: '#4A5561' }}>Nobody has had a text for {scope === 'week' ? 'this week' : 'that day'} yet, so everyone counts as changed.</div>}
      {done && <div className="sharebar" style={{ background: '#EEF7EE', borderColor: '#BDE8CD' }}>✓ {results.length - failed} sent{failed ? ` · ${failed} failed (check the number, then Retry)` : ''}</div>}
      {err && <div className="errbar">{err}</div>}
      <div className="smsgrid">
        <div className="smslist">
          {rows.map((r) => (
            <div key={r.id} className="smsrow">
              <input type="checkbox" checked={r.on} disabled={!r.eligible || !pick} aria-label={`Text ${r.name}`} onChange={() => setSkip((s) => ({ ...s, [r.id]: !s[r.id] }))} />
              <button className="smsname" onClick={() => setPreview(r.id)}>{r.name}</button>
              <span className="mono" style={{ fontSize: 12, color: '#4A5561' }}>{fmtPhone(r.phone)}</span>
              <span className={r.noteCls}>{r.note}</span><span style={{ flex: '1 1 auto' }} />
              <span className={r.statusCls}>{r.status}</span>
              {done?.[r.id]?.status === 'failed' && !r.block && <button className="btn2" disabled={busy} onClick={() => void send([r.id])} style={{ minHeight: 26, padding: '0 8px' }}>Retry</button>}
            </div>
          ))}
          {d && rows.length === 0 && <div className="smsrow">No employees at {storeName} yet.</div>}
        </div>
        <div className="smspreview">
          {pv && <><div style={{ fontSize: 12, color: '#4A5561', marginBottom: 6 }}>{pv.name} will get:</div><div className="bubble">{pv.body}</div></>}
          {pick && <div style={{ fontSize: 12, color: '#8A939C', marginTop: 8 }}>Click a name to preview their text.</div>}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Close</button>
        {pick && <button className="tbtn primary" style={{ minWidth: 140 }} disabled={sendIds.length === 0 || busy || !d?.configured}
          onClick={() => setConfirm({
            title: `Send ${sendIds.length} text${sendIds.length === 1 ? '' : 's'} now?`,
            text: `This texts ${scopeName} to ${sendIds.length} ${storeName} employee${sendIds.length === 1 ? '' : 's'} right away. Texts can’t be taken back once sent. Are you sure?`,
            ok: 'Send texts', run: () => send(sendIds),
          })}>Send {sendIds.length} text{sendIds.length === 1 ? '' : 's'}…</button>}
      </div>
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
    </div></div>
  );
}
